import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import useModules from "../hooks/useModules";
import { api, formatApiError } from "../lib/api";
import { useHasPermission } from "../contexts/PermissionsContext";
import { formatDate, statusBadgeClass } from "../lib/domain";
import { rowOpenProps } from "../hooks/useRowOpen";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import RowActions from "../components/RowActions";
import ShowDeletedToggle, { DeletedRowDecorator, DeletedPill } from "../components/ShowDeletedToggle";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import DetailDialog from "../components/DetailDialog";
import DateInput from "../components/DateInput";
import { useScopedModuleId } from "../contexts/ModuleScopeContext";
import { useAuth } from "../contexts/AuthContext";
import { ROLES } from "../lib/rbac";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
// Iter59.p3 — Field policy relaxed to 5 required + rest optional. RULE_EMPTY
// starts every optional field as `null` / empty so the server-side defaults
// kick in on CREATE (`_apply_create_defaults` in backend/shift_rules.py).
const RULE_EMPTY = {
    name: "", module_id: "", worker_category: "",
    start_time: "", end_time: "",
    break_minutes: null,
    break_is_paid: true,   // Iter60.p41 — new default rule.
    grace_period_minutes: null,
    early_departure_grace_min: null,
    weekday_ot_multiplier: null,
    min_payable_hours: null,
    overtime_threshold_hours: null,
    weekend_days: null,
    weekend_rate_multiplier: null,
    holiday_rate_multiplier: null,
    // Iter60.p45 — Per-day schedule overrides map. Empty by default.
    per_day_overrides: {},
    // Iter60.p57 — Strict shift-window pay policy toggles. Both OFF
    // on NEW rules by default; admins opt in per rule.
    clamp_worked_hours_to_shift_window: false,
    deduct_early_departure_beyond_grace: false,
    // Iter61 — Authorized-OT model. OFF by default; auto-OT is off
    // unless a Shift Rule explicitly opts in.
    allow_auto_ot_after_shift: false,
    effective_date: "",
    status: "active",
};

// Iter59.p3 — helper to bind a numeric input to a nullable state field:
//  - empty input string → null (so server default kicks in on CREATE and
//    PATCH treats it as "field not being updated")
//  - non-empty         → Number(x)
const _numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));


// Iter60.p66 — Origin chip helper for the Public Holidays table.
const ORIGIN_STYLE = {
    seeded: { bg: "rgba(27, 42, 86, 0.10)", fg: "var(--asl-navy)", label: "seeded" },
    gazette: { bg: "rgba(228, 155, 45, 0.16)", fg: "#8f5b0a", label: "gazette" },
    manual: { bg: "rgba(0,0,0,0.05)", fg: "var(--asl-text-3)", label: "manual" },
};
function OriginChip({ origin }) {
    const s = ORIGIN_STYLE[origin] || ORIGIN_STYLE.manual;
    return (
        <span
            className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold"
            style={{ background: s.bg, color: s.fg }}
            title={
                origin === "seeded"
                    ? "Permanent Gregorian holiday seeded on install."
                    : origin === "gazette"
                    ? "Imported from Federal Gazette bulk import."
                    : "Manually added by admin/HR."
            }
            data-testid={`hol-origin-${origin || "manual"}`}
        >
            {s.label}
        </span>
    );
}


// Iter60.p66 — Parse a bulk-import CSV blob (Name,Date,Modules,Note).
// Modules column may be "ALL" or empty for global, or a comma-separated
// list of module CODES. `modulesByCode` maps CODE → module_id.
function parseBulkCsv(raw, modulesByCode) {
    const lines = String(raw || "").split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
    const rows = [];
    const errors = [];
    lines.forEach((line, idx) => {
        // Skip a header row like "Name,Date,..."
        if (idx === 0 && /^name\s*,\s*date/i.test(line)) return;
        // Split on commas; tolerate spaces around fields.
        const parts = line.split(",").map((s) => s.trim());
        if (parts.length < 2) {
            errors.push({ index: idx + 1, error: `Need at least Name,Date. Got: "${line}"` });
            return;
        }
        const [name, date, modulesRaw, note] = parts;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            errors.push({ index: idx + 1, error: `Bad date "${date}" (expected YYYY-MM-DD)` });
            return;
        }
        let applies_to_modules = [];
        if (modulesRaw && modulesRaw.toUpperCase() !== "ALL") {
            const codes = modulesRaw.split(/[|;]/).map((s) => s.trim().toUpperCase()).filter(Boolean);
            for (const c of codes) {
                const mid = modulesByCode?.[c];
                if (!mid) {
                    errors.push({ index: idx + 1, error: `Unknown module code "${c}"` });
                    return;
                }
                applies_to_modules.push(mid);
            }
        }
        rows.push({ name, date, applies_to_modules, note: note || null, origin: "gazette" });
    });
    return { rows, errors };
}


export default function ShiftRules() {
    const navigate = useNavigate();
    const { modules, byId: modulesById } = useModules();
    // Iter59.p12 — Module-scoped permission gates. When the page is opened
    // inside a module workspace (`/m/{code}/shift-rules`) `scopedId` is the
    // active module's id. Passing it into `useHasPermission` lets a Dept
    // Manager see the Add/Edit/Delete controls for their own module — the
    // backend still enforces the "own module only" scope on each write.
    const scopedId = useScopedModuleId();
    const { user } = useAuth();
    // Iter59.p12 — A Department Manager can only manage holidays for their
    // OWN module. Backend enforces this too, but the UI restricts the
    // "Applies to modules" picker and prefills scope to prevent 403s.
    const isDeptMgr = user?.role === ROLES.ASL_DEPT_MGR;
    const canEdit = useHasPermission("shift_rules.write", scopedId || null);
    const canDeleteShift = useHasPermission("shift_rules.delete", scopedId || null);
    const canManageHolidays = useHasPermission("holidays.write", scopedId || null);
    const canDeleteHoliday = useHasPermission("holidays.delete", scopedId || null);
    const [rules, setRules] = useState([]);
    const [editingHoliday, setEditingHoliday] = useState(null);
    const [editingRule, setEditingRule] = useState(null);
    const [holidays, setHolidays] = useState([]);
    const [year, setYear] = useState(new Date().getFullYear());
    // Iter60.p60 Part B — Month dropdown. "all" = every month of `year`
    // (legacy behaviour). "01".."12" narrows the API to that month via
    // the new `month=YYYY-MM` query param on /api/public-holidays.
    const [month, setMonth] = useState("all");
    const [editRule, setEditRule] = useState(null);
    const [holForm, setHolForm] = useState({ date: "", name: "", applies_to_modules: [] });
    const [showAddHol, setShowAddHol] = useState(false);
    const [addHolBusy, setAddHolBusy] = useState(false);
    const [showDeleted, setShowDeleted] = useState(false);
    // Iter59.p2 — dedicated toggle so Public Holidays can show soft-deleted
    // rows independently of the Shift Rules tab.
    const [showDeletedHol, setShowDeletedHol] = useState(false);
    // Iter59.p2 — inline editor for a public holiday (date + name only).
    const [editHol, setEditHol] = useState(null);
    // Iter59.p2 — confirmation state for the delete flow.
    const [holToDelete, setHolToDelete] = useState(null);
    const [holBusy, setHolBusy] = useState(false);
    // Iter60.p66 — Federal Gazette bulk import
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkText, setBulkText] = useState("");
    const [bulkPreview, setBulkPreview] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);

    const load = async () => {
        try {
            // Iter60.p7 — strict per-module scoping. When rendered inside a
            // module workspace (`/m/{code}/shift-rules`) we ONLY fetch that
            // module's shift rules and holidays. Cross-module leakage was
            // the root cause of AP/CN workspaces showing IC's rules.
            //
            // scopedId comes from the URL segment via ModuleScopeContext.
            // Admin/HR viewing at the global /shift-rules route (no
            // module segment) get scopedId=null and the backend returns
            // cross-module data as before.
            const shiftParams = {
                ...(scopedId ? { module_id: scopedId } : {}),
                ...(showDeleted ? { include_deleted: true } : {}),
            };
            const holParams = {
                // Iter60.p60 Part B — Prefer `month=YYYY-MM` when a
                // specific month is chosen; fall back to full-year mode
                // when the user picks "All months".
                ...(month === "all"
                    ? { year }
                    : { month: `${year}-${String(month).padStart(2, "0")}` }),
                ...(scopedId ? { module_id: scopedId } : {}),
                ...(showDeletedHol ? { include_deleted: true } : {}),
            };
            const [r, h] = await Promise.all([
                api.get("/shift-rules", { params: shiftParams }),
                api.get("/public-holidays", { params: holParams }),
            ]);
            setRules(r.data); setHolidays(h.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, [year, month, showDeleted, showDeletedHol, scopedId]);

    const saveRule = async (e) => {
        e.preventDefault();
        // Iter59.p3 — client-side guard for the 5 required fields. Backend
        // will also reject, but this short-circuits before any network call.
        const REQ = [
            ["name", "Name"],
            ["module_id", "Module"],
            ["worker_category", "Worker category"],
            ["start_time", "Start time"],
            ["end_time", "End time"],
        ];
        for (const [k, label] of REQ) {
            const v = editRule[k];
            if (v === null || v === undefined || (typeof v === "string" && !v.trim())) {
                toast.error(`${label} is required`);
                return;
            }
        }
        try {
            const payload = { ...editRule };
            payload.module_id = payload.module_id || null;
            // Iter59.p3 — if the user didn't touch weekend_days on CREATE we
            // default to Friday-only. On PATCH we leave it alone (null → skip).
            if (payload.weekend_days && Array.isArray(payload.weekend_days)) {
                payload.weekend_days = payload.weekend_days.map(Number);
            } else if (!editRule.id) {
                payload.weekend_days = [5];
            }
            if (editRule.id) {
                await api.patch(`/shift-rules/${editRule.id}`, payload);
                toast.success("Rule updated");
            } else {
                await api.post("/shift-rules", payload);
                toast.success("Rule created");
            }
            setEditRule(null); load();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const deactivate = async (r) => {
        try { await api.post(`/shift-rules/${r.id}/deactivate`); toast.success("Deactivated"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    // Iter59.p8 — UI symmetry with `deactivate`. Flips status back to
    // "active" via the new backend endpoint. Deleted rows must be restored
    // first (backend rejects with a clear 400 in that case).
    const activate = async (r) => {
        try { await api.post(`/shift-rules/${r.id}/activate`); toast.success("Activated"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const addHoliday = async (e) => {
        e.preventDefault();
        const name = (holForm.name || "").trim();
        if (!holForm.date) { toast.error("Date is required"); return; }
        if (!name) { toast.error("Name is required"); return; }
        setAddHolBusy(true);
        try {
            await api.post("/public-holidays", {
                date: holForm.date,
                name,
                applies_to_modules: holForm.applies_to_modules || [],
            });
            toast.success(`Holiday "${name}" added on ${formatDate(holForm.date)}`);
            setHolForm({ date: "", name: "", applies_to_modules: [] });
            setShowAddHol(false);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setAddHolBusy(false); }
    };
    // Iter59.p11 — open the Add Holiday modal with today prefilled.
    // Iter59.p12 — For a Dept Manager the "applies to modules" MUST be their
    // own module (backend enforces this); prefill it so the modal is 1-click.
    const openAddHoliday = () => {
        const today = new Date().toISOString().slice(0, 10);
        const initialScope = isDeptMgr && scopedId ? [scopedId] : [];
        setHolForm({ date: today, name: "", applies_to_modules: initialScope });
        setShowAddHol(true);
    };
    const toggleAppliesModule = (mid) => {
        setHolForm((f) => {
            const cur = f.applies_to_modules || [];
            return {
                ...f,
                applies_to_modules: cur.includes(mid)
                    ? cur.filter((x) => x !== mid)
                    : [...cur, mid],
            };
        });
    };
    // Iter59.p2 — inline edit save (PATCH). Prefilled by the "Edit" button.
    const saveHoliday = async (e) => {
        e.preventDefault();
        if (!editHol) return;
        const trimmed = (editHol.name || "").trim();
        if (!trimmed) { toast.error("Name is required"); return; }
        if (!editHol.date) { toast.error("Date is required"); return; }
        setHolBusy(true);
        try {
            await api.patch(`/public-holidays/${editHol.id}`, { date: editHol.date, name: trimmed });
            toast.success("Holiday updated");
            setEditHol(null); load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setHolBusy(false); }
    };
    // Iter59.p2 — soft delete with confirmation (mirrors the RowActions pattern
    // used by Shift Rules). We use a dedicated dialog so the message can spell
    // out the date + name and mention Show deleted for restore.
    const confirmDeleteHoliday = async () => {
        if (!holToDelete) return;
        setHolBusy(true);
        try {
            await api.delete(`/public-holidays/${holToDelete.id}`);
            toast.success(`Deleted — ${holToDelete.name}`);
            setHolToDelete(null); load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setHolBusy(false); }
    };
    const restoreHoliday = async (h) => {
        try { await api.post(`/public-holidays/${h.id}/restore`); toast.success("Restored"); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    const toggleWeekendDay = (d) => {
        setEditRule((r) => {
            const days = r.weekend_days || [];
            return { ...r, weekend_days: days.includes(d) ? days.filter((x) => x !== d) : [...days, d] };
        });
    };

    return (
        <AppShell title="Shift Rules & Holidays">
            <section className="space-y-5" data-testid="shift-rules-page">
                <Tabs defaultValue="rules">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <TabsList>
                            <TabsTrigger value="rules" data-testid="sr-tab-rules">Shift Rules</TabsTrigger>
                            <TabsTrigger value="holidays" data-testid="sr-tab-holidays">Public Holidays</TabsTrigger>
                        </TabsList>
                    </div>

                    <TabsContent value="rules">
                        <div className="flex items-center justify-between my-3">
                            <ShowDeletedToggle value={showDeleted} onChange={setShowDeleted} testid="sr-show-deleted" />
                            {canEdit && (
                                <Button size="sm" className="asl-navy-bg text-white" onClick={() => setEditRule({ ...RULE_EMPTY, module_id: scopedId || "" })} data-testid="sr-new-button">
                                    <Plus className="h-4 w-4 mr-1" /> New Shift Rule
                                </Button>
                            )}
                        </div>
                        <div className="asl-card overflow-hidden">
                            <table className="asl-table asl-table--zebra" data-testid="shift-rules-table">
                                <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                    style={{ background: "var(--asl-navy-50)", borderBottom: "1px solid var(--asl-border)" }}>
                                    <tr>
                                        <th className="text-left px-4 py-3">Name</th>
                                        <th className="text-left px-4 py-3">Module</th>
                                        <th className="text-left px-4 py-3">Hours</th>
                                        <th className="text-left px-4 py-3">Break / Grace</th>
                                        <th className="text-left px-4 py-3">OT after</th>
                                        <th className="text-left px-4 py-3">Weekend</th>
                                        <th className="text-left px-4 py-3">Multipliers</th>
                                        <th className="text-left px-4 py-3">Status</th>
                                        {canEdit && <th className="text-right px-4 py-3">Actions</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rules.map((r) => (
                                        <tr key={r.id} {...rowOpenProps(navigate, () => setEditingRule(r), { testid: `sr-row-${r.id}`, className: 'border-t' })} style={{ borderColor: "var(--asl-border)" }}>
                                            <td className="px-4 py-3 font-medium asl-navy-text">
                                                <DeletedRowDecorator deleted={!!r.deleted_at}>{r.name}</DeletedRowDecorator>
                                                {r.deleted_at && <DeletedPill deletedAt={r.deleted_at} />}
                                            </td>
                                            <td className="px-4 py-3 text-xs">{r.module_id ? modulesById[r.module_id]?.code : "Global"}</td>
                                            <td className="px-4 py-3 mono text-xs">
                                                {r.start_time}–{r.end_time}
                                                {/* Iter60.p45 — Per-day override summary chip.
                                                    E.g. `+ Thu 07:00–15:00` when a Thursday
                                                    override is set. Multi-day → `+ 2 days`. */}
                                                {(() => {
                                                    const pdo = r.per_day_overrides || {};
                                                    const keys = Object.keys(pdo).filter((k) => pdo[k] && Object.keys(pdo[k]).length > 0);
                                                    if (keys.length === 0) return null;
                                                    const labelDayShort = { sun: "Sun", mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat" };
                                                    const summary = keys.length === 1
                                                        ? `${labelDayShort[keys[0]] || keys[0]} ${pdo[keys[0]].start_time || r.start_time}–${pdo[keys[0]].end_time || r.end_time}`
                                                        : `${keys.length} day${keys.length === 1 ? "" : "s"}`;
                                                    return (
                                                        <span
                                                            className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded"
                                                            style={{
                                                                background: "rgba(168, 50, 74, 0.10)",
                                                                color: "var(--asl-crimson, #A8324A)",
                                                                border: "1px solid rgba(168, 50, 74, 0.18)",
                                                            }}
                                                            data-testid={`sr-per-day-chip-${r.id}`}
                                                            title={keys.map((k) => `${labelDayShort[k] || k}: ${pdo[k].start_time || r.start_time}–${pdo[k].end_time || r.end_time}`).join("\n")}
                                                        >
                                                            + {summary}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-4 py-3 mono text-xs">
                                                {r.break_minutes}m / {r.grace_period_minutes}m
                                                {r.break_is_paid !== false && (r.break_minutes || 0) > 0 && (
                                                    <span
                                                        className="ml-1 inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded asl-navy-bg text-white"
                                                        data-testid={`sr-break-paid-chip-${r.id}`}
                                                        title="Break time is counted as paid working time"
                                                    >
                                                        Paid
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 mono text-xs">{r.overtime_threshold_hours}h</td>
                                            <td className="px-4 py-3 text-xs">{(r.weekend_days || []).map((d) => DOW[d]).join(", ")}</td>
                                            <td className="px-4 py-3 mono text-xs">WE {r.weekend_rate_multiplier}× / Hol {r.holiday_rate_multiplier}×</td>
                                            <td className="px-4 py-3">
                                                <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(r.status === "active" ? "approved" : "inactive")}`}>{r.status}</span>
                                            </td>
                                            {canEdit && (
                                                <td className="px-4 py-3 text-right" data-no-row-click>
                                                    <div className="flex items-center justify-end gap-1">
                                                        {!r.deleted_at && (
                                                            <Button size="sm" variant="outline" onClick={() => setEditRule({ ...r })} data-testid={`sr-edit-${r.id}`}>Edit</Button>
                                                        )}
                                                        <RowActions
                                                            entity="shift_rules"
                                                            endpoint="/shift-rules"
                                                            id={r.id}
                                                            label={r.name}
                                                            deleted={!!r.deleted_at}
                                                            moduleId={r.module_id}
                                                            hardDeleteSupported={false}
                                                            onChange={load}
                                                            testid={`sr-actions-${r.id}`}
                                                        />
                                                        {r.status === "active" && !r.deleted_at && (
                                                            <Button size="sm" variant="outline" onClick={() => deactivate(r)} data-testid={`sr-deactivate-${r.id}`}>Deactivate</Button>
                                                        )}
                                                        {r.status === "inactive" && !r.deleted_at && (
                                                            <Button
                                                                size="sm"
                                                                className="asl-navy-bg text-white"
                                                                onClick={() => activate(r)}
                                                                data-testid={`sr-activate-${r.id}`}
                                                            >
                                                                Activate
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </TabsContent>

                    <TabsContent value="holidays">
                        <div className="flex items-center justify-between my-3 gap-3 flex-wrap">
                            <div className="flex items-center gap-3 flex-wrap">
                                <div className="flex items-center gap-2">
                                    <Label className="text-xs">Year</Label>
                                    <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-28" data-testid="hol-year" />
                                </div>
                                {/* Iter60.p60 Part B — Month dropdown. */}
                                <div className="flex items-center gap-2">
                                    <Label className="text-xs">Month</Label>
                                    <Select value={month} onValueChange={setMonth}>
                                        <SelectTrigger className="w-36" data-testid="hol-month">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all" data-testid="hol-month-all">All months</SelectItem>
                                            {[
                                                ["01", "January"], ["02", "February"], ["03", "March"],
                                                ["04", "April"], ["05", "May"], ["06", "June"],
                                                ["07", "July"], ["08", "August"], ["09", "September"],
                                                ["10", "October"], ["11", "November"], ["12", "December"],
                                            ].map(([val, label]) => (
                                                <SelectItem key={val} value={val} data-testid={`hol-month-${val}`}>
                                                    {label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <ShowDeletedToggle value={showDeletedHol} onChange={setShowDeletedHol} testid="hol-show-deleted" />
                            </div>
                            {canManageHolidays && (
                                <div className="flex items-center gap-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => { setBulkOpen(true); setBulkPreview(null); setBulkResult(null); setBulkText(""); }}
                                        data-testid="hol-bulk-import-button"
                                    >
                                        <Upload className="h-4 w-4 mr-1" /> Bulk import
                                    </Button>
                                    <Button
                                        size="sm"
                                        className="asl-navy-bg text-white"
                                        onClick={openAddHoliday}
                                        data-testid="hol-add-button"
                                    >
                                        <Plus className="h-4 w-4 mr-1" /> Add Holiday
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div className="asl-card overflow-hidden">
                            {holidays.length === 0 ? (
                                <div className="p-6 text-sm text-[color:var(--asl-muted)]" data-testid="hol-empty">
                                    {month === "all"
                                        ? `No holidays in ${year}.`
                                        : `No holidays in ${new Date(year, Number(month) - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" })}.`}
                                </div>
                            ) : (
                                <table className="asl-table asl-table--zebra" data-testid="holidays-table">
                                    <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]" style={{ background: "var(--asl-navy-50)" }}>
                                        <tr>
                                            <th className="text-left px-4 py-3">Date</th>
                                            <th className="text-left px-4 py-3">Name</th>
                                            <th className="text-left px-4 py-3">Origin</th>
                                            {(canManageHolidays || canDeleteHoliday) && <th className="text-right px-4 py-3">Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {holidays.map((h) => (
                                            <tr key={h.id} {...rowOpenProps(navigate, () => setEditingHoliday(h), { testid: `hol-row-${h.id}`, className: 'border-t' })} style={{ borderColor: "var(--asl-border)" }}>
                                                <td className="px-4 py-3 mono text-xs">
                                                    <DeletedRowDecorator deleted={!!h.deleted_at}>{formatDate(h.date)}</DeletedRowDecorator>
                                                </td>
                                                <td className="px-4 py-3 asl-navy-text font-medium">
                                                    <DeletedRowDecorator deleted={!!h.deleted_at}>{h.name}</DeletedRowDecorator>
                                                    {h.deleted_at && <DeletedPill deletedAt={h.deleted_at} />}
                                                </td>
                                                {/* Iter60.p66 — Origin chip: seeded / gazette / manual */}
                                                <td className="px-4 py-3">
                                                    <OriginChip origin={h.origin} />
                                                </td>
                                                {(canManageHolidays || canDeleteHoliday) && (
                                                    <td className="px-4 py-3 text-right" data-no-row-click>
                                                        <div className="flex items-center justify-end gap-1">
                                                            {!h.deleted_at && canManageHolidays && (
                                                                <Button size="sm" variant="outline"
                                                                    onClick={() => setEditHol({ id: h.id, date: h.date, name: h.name })}
                                                                    data-testid={`hol-edit-${h.id}`}>Edit</Button>
                                                            )}
                                                            {!h.deleted_at && canDeleteHoliday && (
                                                                <Button size="sm" variant="outline"
                                                                    onClick={() => setHolToDelete(h)}
                                                                    data-testid={`hol-del-${h.id}`}>
                                                                    <Trash2 className="h-3 w-3" />
                                                                </Button>
                                                            )}
                                                            {h.deleted_at && canManageHolidays && (
                                                                <Button size="sm" variant="outline"
                                                                    onClick={() => restoreHoliday(h)}
                                                                    data-testid={`hol-restore-${h.id}`}>Restore</Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </TabsContent>
                </Tabs>
            </section>

            <Dialog open={!!editRule} onOpenChange={(o) => !o && setEditRule(null)}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sr-dialog">
                    <DialogHeader><DialogTitle>{editRule?.id ? "Edit Shift Rule" : "New Shift Rule"}</DialogTitle></DialogHeader>
                    {editRule && (
                        <form onSubmit={saveRule} className="grid grid-cols-2 gap-3 pt-2">
                            <Field label="Name *"><Input required value={editRule.name} onChange={(e) => setEditRule((r) => ({ ...r, name: e.target.value }))} data-testid="sr-name" /></Field>
                            <Field label="Module *">
                                {/* Iter60.p13 — When creating from a module workspace
                                    the module is preset via `editRule.module_id = scopedId`
                                    and MUST stay locked (also enforced immutable via PATCH
                                    on the backend per p7). Disable the selector so users
                                    can't accidentally switch. */}
                                <Select
                                    value={editRule.module_id || ""}
                                    onValueChange={(v) => setEditRule((r) => ({ ...r, module_id: v }))}
                                    disabled={!!scopedId || !!editRule.id}
                                >
                                    <SelectTrigger data-testid="sr-module"><SelectValue placeholder="Select a module" /></SelectTrigger>
                                    <SelectContent>
                                        {modules.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                {(!!scopedId || !!editRule.id) && (
                                    <div className="text-[10px] text-[color:var(--asl-muted)] mt-1" data-testid="sr-module-locked-hint">
                                        {editRule.id ? "Module is immutable after creation." : "Locked to current module workspace."}
                                    </div>
                                )}
                            </Field>
                            <Field label="Worker category *">
                                <Select value={editRule.worker_category || ""} onValueChange={(v) => setEditRule((r) => ({ ...r, worker_category: v }))}>
                                    <SelectTrigger data-testid="sr-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="casual">Casual</SelectItem>
                                        <SelectItem value="non_casual_manual">Non-Casual Manual</SelectItem>
                                        <SelectItem value="non_casual_non_manual">Non-Casual Non-Manual</SelectItem>
                                        <SelectItem value="any">Any (all categories)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Effective date"><DateInput value={editRule.effective_date || ""} onChange={(v) => setEditRule((r) => ({ ...r, effective_date: v }))} data-testid="sr-effective" /></Field>
                            <Field label="Start time *"><Input type="time" required value={editRule.start_time || ""} onChange={(e) => setEditRule((r) => ({ ...r, start_time: e.target.value }))} data-testid="sr-start" /></Field>
                            <Field label="End time *"><Input type="time" required value={editRule.end_time || ""} onChange={(e) => setEditRule((r) => ({ ...r, end_time: e.target.value }))} data-testid="sr-end" /></Field>
                            <Field label="Break (min)"><Input type="number" min="0" placeholder="0" value={editRule.break_minutes ?? ""} onChange={(e) => setEditRule((r) => ({ ...r, break_minutes: _numOrNull(e.target.value) }))} data-testid="sr-break" /></Field>
                            {/* Iter60.p41 — Break-is-paid toggle. Default true;
                                keeps break time inside worked_minutes. Flip off
                                when break should be subtracted from worked hours. */}
                            <Field label="Break is paid (included in worked hours)">
                                <label
                                    className="flex items-center gap-2 h-9 px-2 border rounded-md bg-white"
                                    style={{ borderColor: "var(--asl-border)" }}
                                    data-testid="sr-break-is-paid-wrap"
                                >
                                    <input
                                        type="checkbox"
                                        checked={editRule.break_is_paid !== false}
                                        onChange={(e) => setEditRule((r) => ({ ...r, break_is_paid: e.target.checked }))}
                                        data-testid="sr-break-is-paid"
                                    />
                                    <span className="text-xs asl-navy-text">
                                        {editRule.break_is_paid !== false
                                            ? "Break counts as paid working time"
                                            : "Break is subtracted from worked hours"}
                                    </span>
                                </label>
                            </Field>
                            <Field label="Grace (min)"><Input type="number" min="0" placeholder="0" value={editRule.grace_period_minutes ?? ""} onChange={(e) => setEditRule((r) => ({ ...r, grace_period_minutes: _numOrNull(e.target.value) }))} data-testid="sr-grace" /></Field>
                            <Field label="Early Departure grace (min)"><Input type="number" min="0" placeholder="0" value={editRule.early_departure_grace_min ?? ""} onChange={(e) => setEditRule((r) => ({ ...r, early_departure_grace_min: _numOrNull(e.target.value) }))} data-testid="sr-ed-grace" /></Field>
                            <Field label="Min payable hrs"><Input type="number" step="0.5" min="0" placeholder="0" value={editRule.min_payable_hours ?? ""} onChange={(e) => setEditRule((r) => ({ ...r, min_payable_hours: _numOrNull(e.target.value) }))} data-testid="sr-min-payable" /></Field>
                            <Field label="OT after hrs"><Input type="number" step="0.5" min="0" placeholder="blank = no OT" value={editRule.overtime_threshold_hours ?? ""} onChange={(e) => setEditRule((r) => ({ ...r, overtime_threshold_hours: _numOrNull(e.target.value) }))} data-testid="sr-ot-after" /></Field>
                            <Field label="Weekday OT ×"><Input type="number" step="0.1" min="1" placeholder="1.0" value={editRule.weekday_ot_multiplier ?? ""} onChange={(e) => setEditRule((r) => ({ ...r, weekday_ot_multiplier: _numOrNull(e.target.value) }))} data-testid="sr-weekday-ot" /></Field>
                            {/* Iter60.p57 — Strict shift-window pay policy toggles.
                                Both default OFF for backwards compat. See
                                `_clamp_window_iso` in backend/attendance.py and
                                the `deduct_early_departure_beyond_grace` gate in
                                backend/payroll.py for the read side. */}
                            <div className="col-span-2 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="sr-p57-toggles">
                                <Field label="Clamp worked hours to shift window">
                                    <label
                                        className="flex items-start gap-2 p-2 border rounded-md bg-white cursor-pointer"
                                        style={{ borderColor: "var(--asl-border)" }}
                                        data-testid="sr-clamp-window-wrap"
                                    >
                                        <input
                                            type="checkbox"
                                            className="mt-0.5"
                                            checked={!!editRule.clamp_worked_hours_to_shift_window}
                                            onChange={(e) => setEditRule((r) => ({ ...r, clamp_worked_hours_to_shift_window: e.target.checked }))}
                                            data-testid="sr-clamp-window"
                                        />
                                        <span className="text-xs leading-snug asl-navy-text">
                                            <div className="font-semibold">
                                                {editRule.clamp_worked_hours_to_shift_window ? "On" : "Off"}
                                            </div>
                                            <div className="text-[10px] text-[color:var(--asl-muted)]">
                                                When on, minutes before start time or after end time are ignored for regular pay. Does NOT control OT — OT behavior is controlled by the "Auto-OT after scheduled shift" toggle.
                                            </div>
                                        </span>
                                    </label>
                                </Field>
                                <Field label="Deduct pay for early departure beyond grace">
                                    <label
                                        className="flex items-start gap-2 p-2 border rounded-md bg-white cursor-pointer"
                                        style={{ borderColor: "var(--asl-border)" }}
                                        data-testid="sr-deduct-early-wrap"
                                    >
                                        <input
                                            type="checkbox"
                                            className="mt-0.5"
                                            checked={!!editRule.deduct_early_departure_beyond_grace}
                                            onChange={(e) => setEditRule((r) => ({ ...r, deduct_early_departure_beyond_grace: e.target.checked }))}
                                            data-testid="sr-deduct-early"
                                        />
                                        <span className="text-xs leading-snug asl-navy-text">
                                            <div className="font-semibold">
                                                {editRule.deduct_early_departure_beyond_grace ? "On" : "Off"}
                                            </div>
                                            <div className="text-[10px] text-[color:var(--asl-muted)]">
                                                When on, leaving early by more than the grace window reduces base pay proportionally. Excused early departures are still exempt. Does NOT control OT.
                                            </div>
                                        </span>
                                    </label>
                                </Field>
                                {/* Iter61 — Authorized-OT toggle. Auto-OT is
                                    killed by default; managers add OT
                                    entries explicitly via the Attendance
                                    kebab / Bulk OT dialog. */}
                                <Field label="Auto-OT after scheduled shift">
                                    <label
                                        className="flex items-start gap-2 p-2 border rounded-md bg-white cursor-pointer"
                                        style={{ borderColor: "var(--asl-border)" }}
                                        data-testid="sr-allow-auto-ot-wrap"
                                    >
                                        <input
                                            type="checkbox"
                                            className="mt-0.5"
                                            checked={!!editRule.allow_auto_ot_after_shift}
                                            onChange={(e) => setEditRule((r) => ({ ...r, allow_auto_ot_after_shift: e.target.checked }))}
                                            data-testid="sr-allow-auto-ot"
                                        />
                                        <span className="text-xs leading-snug asl-navy-text">
                                            <div className="font-semibold">
                                                {editRule.allow_auto_ot_after_shift ? "On" : "Off"}
                                            </div>
                                            <div className="text-[10px] text-[color:var(--asl-muted)]">
                                                When on, minutes worked AFTER the scheduled end time (beyond departure grace) automatically count as OT at the shift's OT multiplier. When off, OT is added manually by a manager only.
                                            </div>
                                        </span>
                                    </label>
                                </Field>
                            </div>
                            <Field label="Weekend ×"><Input type="number" step="0.1" min="1" placeholder="1.0" value={editRule.weekend_rate_multiplier ?? ""} onChange={(e) => setEditRule((r) => ({ ...r, weekend_rate_multiplier: _numOrNull(e.target.value) }))} data-testid="sr-weekend-mult" /></Field>
                            <Field label="Holiday ×"><Input type="number" step="0.1" min="1" placeholder="1.0" value={editRule.holiday_rate_multiplier ?? ""} onChange={(e) => setEditRule((r) => ({ ...r, holiday_rate_multiplier: _numOrNull(e.target.value) }))} data-testid="sr-holiday-mult" /></Field>
                            <div className="col-span-2 space-y-1">
                                <Label className="text-xs uppercase">Weekend days</Label>
                                <div className="flex gap-1 flex-wrap" data-testid="sr-weekend-days">
                                    {DOW.map((name, idx) => (
                                        <button type="button" key={idx} onClick={() => toggleWeekendDay(idx)}
                                            className={`text-xs px-3 py-1 rounded-full border ${(editRule.weekend_days || []).includes(idx) ? "asl-navy-bg text-white border-transparent" : "bg-white"}`}
                                            data-testid={`sr-weekend-${idx}`}>{name}</button>
                                    ))}
                                </div>
                                <div className="text-[10px] text-[color:var(--asl-muted)]">Leave empty to default to Friday.</div>
                            </div>
                            {/* Iter60.p45 — Per-day schedule overrides.
                                Collapsed by default. When a row is enabled the
                                inputs default to the rule's base values so the
                                operator only edits what changes (typically
                                end_time on Thursday). Weekend days show a
                                muted "weekend — hours ignored" note. */}
                            <PerDayOverridesSection
                                editRule={editRule}
                                setEditRule={setEditRule}
                            />
                            <DialogFooter className="col-span-2">
                                <Button type="button" variant="outline" onClick={() => setEditRule(null)}>Cancel</Button>
                                <Button type="submit" className="asl-navy-bg text-white" data-testid="sr-submit">Save shift rule</Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        
            <DetailDialog row={editingRule} onClose={() => setEditingRule(null)} title="Shift Rule" testid="editingRule-detail" />

            <DetailDialog row={editingHoliday} onClose={() => setEditingHoliday(null)} title="Public Holiday" testid="editingHoliday-detail" />

            {/* Iter59.p7.6 — "+ Add Holiday" modal. Date + Name + Applies to
                modules (empty = all modules). Mirrors the "+ New Shift Rule"
                button pattern for visual symmetry. */}
            <Dialog open={showAddHol} onOpenChange={(o) => { if (!o) { setShowAddHol(false); } }}>
                <DialogContent className="max-w-md" data-testid="hol-add-dialog">
                    <DialogHeader><DialogTitle>Add Public Holiday</DialogTitle></DialogHeader>
                    <form onSubmit={addHoliday} className="space-y-3 pt-2">
                        <Field label="Date *">
                            <DateInput required value={holForm.date}
                                onChange={(v) => setHolForm((f) => ({ ...f, date: v }))}
                                data-testid="hol-add-date" />
                        </Field>
                        <Field label="Name *">
                            <Input required maxLength={100} placeholder="e.g. Independence Day"
                                value={holForm.name}
                                onChange={(e) => setHolForm((f) => ({ ...f, name: e.target.value }))}
                                data-testid="hol-add-name" />
                        </Field>
                        <div className="space-y-1">
                            <Label className="text-xs uppercase tracking-wide text-[color:var(--asl-muted)]">
                                Applies to modules
                            </Label>
                            <div className="text-[10px] text-[color:var(--asl-muted)] mb-1">
                                {isDeptMgr
                                    ? "As a Department Manager you can only create holidays for your own module."
                                    : <>Leave all unchecked to apply this holiday to <strong>all modules</strong>.</>}
                            </div>
                            <div className="flex flex-wrap gap-1" data-testid="hol-add-modules">
                                {modules.map((m) => {
                                    const on = (holForm.applies_to_modules || []).includes(m.id);
                                    // Iter59.p12 — Dept managers can only pick their own module.
                                    const disabled = isDeptMgr && m.id !== scopedId;
                                    return (
                                        <button
                                            type="button"
                                            key={m.id}
                                            onClick={() => { if (!disabled) toggleAppliesModule(m.id); }}
                                            disabled={disabled}
                                            className={`text-xs px-3 py-1 rounded-full border ${on ? "asl-navy-bg text-white border-transparent" : "bg-white"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                                            data-testid={`hol-add-mod-${m.code}`}
                                        >
                                            {m.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowAddHol(false)}>Cancel</Button>
                            <Button type="submit" className="asl-navy-bg text-white" disabled={addHolBusy} data-testid="hol-add-submit">
                                {addHolBusy ? "Saving…" : "Save holiday"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Iter59.p2 — inline edit dialog for a public holiday. Date + name only. */}
            <Dialog open={!!editHol} onOpenChange={(o) => !o && setEditHol(null)}>
                <DialogContent className="max-w-md" data-testid="hol-edit-dialog">
                    <DialogHeader><DialogTitle>Edit Public Holiday</DialogTitle></DialogHeader>
                    {editHol && (
                        <form onSubmit={saveHoliday} className="space-y-3 pt-2">
                            <Field label="Date *">
                                <DateInput required value={editHol.date}
                                    onChange={(v) => setEditHol((h) => ({ ...h, date: v }))}
                                    data-testid="hol-edit-date" />
                            </Field>
                            <Field label="Name *">
                                <Input required maxLength={100} value={editHol.name}
                                    onChange={(e) => setEditHol((h) => ({ ...h, name: e.target.value }))}
                                    data-testid="hol-edit-name" />
                            </Field>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setEditHol(null)}>Cancel</Button>
                                <Button type="submit" className="asl-navy-bg text-white" disabled={holBusy} data-testid="hol-edit-submit">
                                    {holBusy ? "Saving…" : "Save holiday"}
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            {/* Iter59.p2 — delete confirmation. Message spells out date + name
                and mentions restore via the Show deleted toggle. */}
            <Dialog open={!!holToDelete} onOpenChange={(o) => !o && setHolToDelete(null)}>
                <DialogContent className="max-w-md" data-testid="hol-delete-dialog">
                    <DialogHeader><DialogTitle>Delete public holiday?</DialogTitle></DialogHeader>
                    {holToDelete && (
                        <div className="space-y-3 text-sm">
                            <p>
                                Delete holiday <strong>&ldquo;{holToDelete.name}&rdquo;</strong> on{" "}
                                <strong>{formatDate(holToDelete.date)}</strong>? This can be restored
                                via <em>Show deleted</em>.
                            </p>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setHolToDelete(null)}>Cancel</Button>
                                <Button type="button" className="bg-red-600 hover:bg-red-700 text-white"
                                    disabled={holBusy} onClick={confirmDeleteHoliday}
                                    data-testid="hol-delete-confirm">
                                    {holBusy ? "Deleting…" : "Delete holiday"}
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Iter60.p66 — Federal Gazette bulk import dialog */}
            <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) { setBulkPreview(null); setBulkResult(null); setBulkText(""); } }}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="hol-bulk-dialog">
                    <DialogHeader>
                        <DialogTitle>Bulk import — Federal Gazette public holidays</DialogTitle>
                    </DialogHeader>
                    {!bulkResult ? (
                        <div className="space-y-3 text-sm">
                            <div className="rounded-md bg-[color:var(--asl-navy-50,#eef1f8)] p-3 text-xs asl-navy-text">
                                <p className="font-semibold mb-1">CSV format</p>
                                <pre className="mono text-[11px] whitespace-pre-wrap">Name,Date,Modules,Note{"\n"}Eid al-Fitr Day 1,2027-03-30,ALL,Federal Gazette 2027{"\n"}Prophet Muhammad Birthday,2027-11-18,AP|CN,Federal Gazette 2027</pre>
                                <p className="mt-1 text-[color:var(--asl-muted)]">
                                    Modules: <span className="mono">ALL</span> or blank = all modules. Otherwise pipe-separated module codes (AP, IC, CN).
                                </p>
                            </div>
                            <textarea
                                rows={8}
                                className="w-full rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2 mono text-xs"
                                placeholder="Paste CSV rows here…"
                                value={bulkText}
                                onChange={(e) => { setBulkText(e.target.value); setBulkPreview(null); }}
                                data-testid="hol-bulk-textarea"
                            />
                            {bulkPreview && (
                                <div className="border border-[color:var(--asl-border,#E5E7EB)] rounded-md" data-testid="hol-bulk-preview">
                                    <div className="px-3 py-2 border-b border-[color:var(--asl-border,#E5E7EB)] text-xs asl-navy-text font-semibold">
                                        Preview: {bulkPreview.rows.length} row(s) parsed
                                        {bulkPreview.errors.length > 0 && (
                                            <span className="asl-crimson-text font-semibold ml-2">
                                                — {bulkPreview.errors.length} error(s)
                                            </span>
                                        )}
                                    </div>
                                    {bulkPreview.rows.length > 0 && (
                                        <table className="w-full text-xs">
                                            <thead className="text-[10px] uppercase text-[color:var(--asl-muted)]">
                                                <tr>
                                                    <th className="px-3 py-1 text-left">Date</th>
                                                    <th className="px-3 py-1 text-left">Name</th>
                                                    <th className="px-3 py-1 text-left">Modules</th>
                                                    <th className="px-3 py-1 text-left">Note</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {bulkPreview.rows.map((r, i) => (
                                                    <tr key={i} className="border-t border-[color:var(--asl-border,#E5E7EB)]">
                                                        <td className="px-3 py-1 mono">{r.date}</td>
                                                        <td className="px-3 py-1">{r.name}</td>
                                                        <td className="px-3 py-1 mono text-[color:var(--asl-muted)]">
                                                            {(r.applies_to_modules || []).length === 0 ? "ALL" : r.applies_to_modules.length + " module(s)"}
                                                        </td>
                                                        <td className="px-3 py-1 text-[color:var(--asl-muted)]">{r.note || "—"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                    {bulkPreview.errors.length > 0 && (
                                        <div className="px-3 py-2 border-t border-[color:var(--asl-border,#E5E7EB)] text-xs asl-crimson-text space-y-1">
                                            {bulkPreview.errors.map((e, i) => (
                                                <div key={i} data-testid={`hol-bulk-row-error-${i}`}>
                                                    Line {e.index}: {e.error}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-3 text-sm" data-testid="hol-bulk-result">
                            <div className="rounded-md bg-[color:var(--asl-navy-50,#eef1f8)] p-3 grid grid-cols-3 gap-3 text-xs">
                                <div>
                                    <div className="text-[color:var(--asl-muted)]">Created</div>
                                    <div className="text-2xl font-bold asl-navy-text" data-testid="hol-bulk-result-created">{bulkResult.created}</div>
                                </div>
                                <div>
                                    <div className="text-[color:var(--asl-muted)]">Skipped duplicates</div>
                                    <div className="text-2xl font-bold" data-testid="hol-bulk-result-skipped">{bulkResult.skipped_duplicates.length}</div>
                                </div>
                                <div>
                                    <div className="text-[color:var(--asl-muted)]">Errors</div>
                                    <div className="text-2xl font-bold asl-crimson-text" data-testid="hol-bulk-result-errors">{bulkResult.errors.length}</div>
                                </div>
                            </div>
                            {bulkResult.skipped_duplicates.length > 0 && (
                                <div className="text-xs space-y-1">
                                    <div className="font-semibold text-[color:var(--asl-muted)]">Skipped duplicates:</div>
                                    {bulkResult.skipped_duplicates.map((s, i) => (
                                        <div key={i} className="mono">
                                            {s.date} — {s.name}: {s.reason}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {bulkResult.errors.length > 0 && (
                                <div className="text-xs space-y-1 asl-crimson-text">
                                    <div className="font-semibold">Errors:</div>
                                    {bulkResult.errors.map((e, i) => (
                                        <div key={i}>Line {e.index}: {e.error}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        {!bulkResult && (
                            <>
                                <Button variant="outline" onClick={() => setBulkOpen(false)} data-testid="hol-bulk-cancel">
                                    Cancel
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        const modsByCode = {};
                                        (modules || []).forEach((m) => { modsByCode[String(m.code || "").toUpperCase()] = m.id; });
                                        setBulkPreview(parseBulkCsv(bulkText, modsByCode));
                                    }}
                                    disabled={!bulkText.trim()}
                                    data-testid="hol-bulk-preview-btn"
                                >
                                    Preview
                                </Button>
                                <Button
                                    className="asl-navy-bg text-white"
                                    disabled={!bulkPreview || bulkPreview.rows.length === 0 || bulkBusy}
                                    onClick={async () => {
                                        if (!bulkPreview) return;
                                        setBulkBusy(true);
                                        try {
                                            const { data } = await api.post(
                                                "/public-holidays/bulk-import",
                                                { items: bulkPreview.rows },
                                            );
                                            setBulkResult({ ...data, errors: [...(data.errors || []), ...(bulkPreview.errors || [])] });
                                            toast.success(`Imported ${data.created} · Skipped ${data.skipped_duplicates.length}`);
                                            load();
                                        } catch (e) {
                                            toast.error(formatApiError(e));
                                        } finally {
                                            setBulkBusy(false);
                                        }
                                    }}
                                    data-testid="hol-bulk-import-btn"
                                >
                                    <Upload className="h-4 w-4 mr-1" />
                                    {bulkBusy ? "Importing…" : bulkPreview ? `Import ${bulkPreview.rows.length}` : "Import"}
                                </Button>
                            </>
                        )}
                        {bulkResult && (
                            <Button onClick={() => setBulkOpen(false)} data-testid="hol-bulk-done">Done</Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
</AppShell>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-[color:var(--asl-muted)]">{label}</Label>
            {children}
        </div>
    );
}


// Iter60.p45 — Per-day schedule overrides section for the Shift Rules
// Edit form. Weekday keys match `PER_DAY_KEY_BY_WEEKDAY_IDX` on the
// backend (short lowercase strings). Order is Sat→Fri to match the
// user's business week (Fri is weekend). Toggling a row ON pre-fills
// with the rule's base values so the operator only edits what changes.
const PER_DAY_ROWS = [
    { key: "sat", label: "Saturday", idx: 5 },
    { key: "sun", label: "Sunday",   idx: 6 },
    { key: "mon", label: "Monday",   idx: 0 },
    { key: "tue", label: "Tuesday",  idx: 1 },
    { key: "wed", label: "Wednesday", idx: 2 },
    { key: "thu", label: "Thursday", idx: 3 },
    { key: "fri", label: "Friday",   idx: 4 },
];

function PerDayOverridesSection({ editRule, setEditRule }) {
    const [open, setOpen] = React.useState(() => {
        // Auto-expand if the rule already carries overrides.
        const pdo = editRule?.per_day_overrides || {};
        return Object.keys(pdo).some((k) => pdo[k] && Object.keys(pdo[k]).length > 0);
    });
    const pdo = editRule.per_day_overrides || {};
    const weekendSet = new Set(editRule.weekend_days || []);

    const setEntry = (key, patch) => {
        setEditRule((r) => {
            const next = { ...(r.per_day_overrides || {}) };
            if (patch === null) {
                delete next[key];
            } else {
                next[key] = { ...(next[key] || {}), ...patch };
            }
            return { ...r, per_day_overrides: next };
        });
    };

    const enableRow = (key) => {
        setEntry(key, {
            start_time: editRule.start_time || "",
            end_time: editRule.end_time || "",
            break_minutes: editRule.break_minutes ?? 0,
            break_is_paid: editRule.break_is_paid !== false,
        });
    };

    const applyThursdayPreset = () => {
        setEntry("thu", {
            start_time: editRule.start_time || "07:00",
            end_time: "15:00",
            break_minutes: editRule.break_minutes ?? 60,
            break_is_paid: editRule.break_is_paid !== false,
        });
        setOpen(true);
    };

    return (
        <div className="col-span-2 space-y-2" data-testid="sr-per-day-section">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full text-left px-3 py-2 rounded-md border text-xs asl-navy-text hover:brightness-95"
                style={{ borderColor: "var(--asl-border)", background: "rgba(27, 42, 86, 0.03)" }}
                data-testid="sr-per-day-toggle"
            >
                <strong>Per-day schedule overrides</strong> (optional) {open ? "▾" : "▸"}
            </button>
            {open && (
                <div className="rounded-md border p-3 space-y-3" style={{ borderColor: "var(--asl-border)" }}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="text-[11px] text-[color:var(--asl-muted)]">
                            Use these when a specific weekday has different hours
                            from the rule's base schedule. Weekend days remain
                            weekends regardless of override.
                            <br />
                            <span className="asl-navy-text">
                                Bonus minutes are added to Payable for{" "}
                                <strong>Present</strong> rows on that weekday.
                                Paid at <strong>1×</strong> and surface as a
                                "Schedule bonus" line on payroll.
                            </span>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={applyThursdayPreset}
                            data-testid="sr-per-day-thu-preset"
                        >
                            Thursday 15:00 preset
                        </Button>
                    </div>
                    <div className="space-y-1.5">
                        {PER_DAY_ROWS.map((row) => {
                            const isWeekend = weekendSet.has(row.idx);
                            const entry = pdo[row.key];
                            const enabled = !!entry && Object.keys(entry).length > 0;
                            return (
                                <div
                                    key={row.key}
                                    className="grid gap-2 items-center"
                                    style={{ gridTemplateColumns: "auto 100px 80px 80px 80px 80px 1fr" }}
                                    data-testid={`sr-per-day-row-${row.key}`}
                                >
                                    <label className="flex items-center gap-2 min-w-[110px]">
                                        <input
                                            type="checkbox"
                                            disabled={isWeekend}
                                            checked={enabled}
                                            onChange={(e) => e.target.checked ? enableRow(row.key) : setEntry(row.key, null)}
                                            data-testid={`sr-per-day-enable-${row.key}`}
                                        />
                                        <span className="text-xs asl-navy-text">{row.label}</span>
                                    </label>
                                    {isWeekend ? (
                                        <div className="col-span-6 text-[10px] text-[color:var(--asl-muted)] italic">
                                            (weekend — hours ignored, bonus not applied)
                                        </div>
                                    ) : (
                                        <>
                                            <Input
                                                type="time"
                                                disabled={!enabled}
                                                value={entry?.start_time ?? (enabled ? editRule.start_time : "")}
                                                onChange={(e) => setEntry(row.key, { start_time: e.target.value })}
                                                data-testid={`sr-per-day-start-${row.key}`}
                                            />
                                            <Input
                                                type="time"
                                                disabled={!enabled}
                                                value={entry?.end_time ?? (enabled ? editRule.end_time : "")}
                                                onChange={(e) => setEntry(row.key, { end_time: e.target.value })}
                                                data-testid={`sr-per-day-end-${row.key}`}
                                            />
                                            <Input
                                                type="number"
                                                min="0"
                                                disabled={!enabled}
                                                placeholder="Break"
                                                value={entry?.break_minutes ?? (enabled ? (editRule.break_minutes ?? 0) : "")}
                                                onChange={(e) => setEntry(row.key, { break_minutes: Number(e.target.value || 0) })}
                                                data-testid={`sr-per-day-break-${row.key}`}
                                            />
                                            <label className="text-[10px] flex items-center gap-1">
                                                <input
                                                    type="checkbox"
                                                    disabled={!enabled}
                                                    checked={enabled ? (entry?.break_is_paid !== false) : false}
                                                    onChange={(e) => setEntry(row.key, { break_is_paid: e.target.checked })}
                                                    data-testid={`sr-per-day-break-paid-${row.key}`}
                                                />
                                                paid
                                            </label>
                                            {/* Iter63 — Schedule bonus inputs. Bonus minutes are
                                                added to Payable at 1× on top of the clamped
                                                base for Present rows on this weekday. */}
                                            <Input
                                                type="number"
                                                min="0"
                                                disabled={!enabled}
                                                placeholder="Bonus"
                                                value={entry?.bonus_minutes ?? ""}
                                                onChange={(e) => setEntry(row.key, { bonus_minutes: Number(e.target.value || 0) })}
                                                data-testid={`sr-per-day-bonus-min-${row.key}`}
                                                title="Bonus minutes added to Payable at 1× for Present rows on this weekday"
                                            />
                                            <Input
                                                type="text"
                                                disabled={!enabled || !(entry?.bonus_minutes > 0)}
                                                placeholder="Schedule bonus"
                                                value={entry?.bonus_label ?? ""}
                                                onChange={(e) => setEntry(row.key, { bonus_label: e.target.value })}
                                                data-testid={`sr-per-day-bonus-label-${row.key}`}
                                                title="Label shown on the payroll breakdown"
                                            />
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
